import { type RefObject, useContext, useLayoutEffect, useMemo } from 'react';
import { useMedia } from 'react-use';
import { useDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { BreakpointQueryLarge } from '@hungryroot/ui-kit';
import { ControlSelect } from '../../components/ControlSelect/ControlSelect';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { type QuizToastObj } from '../../components/QuizToast/quiz-toast';
import { QuizToast } from '../../components/QuizToast/QuizToast/QuizToast';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { ToggleVariant, Toggle } from '../../components/Toggle/Toggle/Toggle';
import { EatingHabit } from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import { eatingHabitsAnalytics } from '../../segment/quizAnalytics';
import eatingHabitsStyles from './EatingHabits.module.css';
import styles from '../layout.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

/**
 * The five eating habits, in render order. `delayOneColumn` / `delayTwoColumn`
 * are the entrance-animation stagger: which one applies depends on how many
 * columns the grid is actually rendering, not on viewport width alone — the
 * Exp 255 mini quiz is two columns at every width.
 */
const OPTIONS = [
  {
    id: EatingHabit.Omnivore,
    label: 'Omnivore',
    slug: 'omnivore',
    subcopy: 'I eat meat + seafood most meals',
    delayOneColumn: 'animation-delay-point-5s',
    delayTwoColumn: 'animation-delay-point-5s',
  },
  {
    id: EatingHabit.Flexitarian,
    label: 'Flexitarian',
    slug: 'flexitarian',
    subcopy: 'I eat meat + seafood some meals',
    delayOneColumn: 'animation-delay-point-5s',
    delayTwoColumn: 'animation-delay-point-5s',
  },
  {
    id: EatingHabit.Pescatarian,
    label: 'Pescatarian',
    slug: 'pescatarian',
    subcopy: 'I don\u2019t eat meat',
    delayOneColumn: 'animation-delay-point-55s',
    delayTwoColumn: 'animation-delay-point-5s',
  },
  {
    id: EatingHabit.Vegetarian,
    label: 'Vegetarian',
    slug: 'vegetarian',
    subcopy: 'I don\u2019t eat meat or seafood',
    delayOneColumn: 'animation-delay-point-6s',
    delayTwoColumn: 'animation-delay-point-55s',
  },
  {
    id: EatingHabit.Vegan,
    label: 'Vegan',
    slug: 'vegan',
    subcopy: 'I don\u2019t eat meat, seafood, dairy, or eggs',
    delayOneColumn: 'animation-delay-point-65s',
    delayTwoColumn: 'animation-delay-point-55s',
  },
] as const;

export function EatingHabits(props: Props) {
  const { testid, scrollRef } = props;
  const { quiz, quizToast, isMiniQuiz, onSubmit } = useContext(QuizContext);

  const { habits } = quiz;

  const isDesktop = useMedia(BreakpointQueryLarge);

  const dietaryRestrictions = useDietaryRestrictions();

  const restrictionsImageMap = useMemo(() => {
    const results = dietaryRestrictions.data?.results;
    if (!results || !Array.isArray(results)) return {};
    return results.reduce(
      (acc, restriction) => {
        acc[restriction.slug] = restriction.thumbnail_url;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [dietaryRestrictions]);

  useLayoutEffect(() => {
    if (!quizToast) {
      return;
    }
    const { clearToasts } = quizToast;

    clearToasts();

    // Auto-advance leaves before this toast can settle, and it then
    // renders on protein. Mini-quiz skips it until that handoff is designed.
    if (isMiniQuiz) {
      return;
    }

    const toastTimeout = setTimeout(() => {
      renderToast(quizToast);
    }, 500);

    return () => {
      clearTimeout(toastTimeout);
    };
  }, [quizToast, isMiniQuiz]);

  const renderToast = (quizToast: QuizToastObj) => {
    const {
      content: { text, icon },
      toast,
    } = quizToast;

    return toast(
      <QuizToast testid={`${testid}-toast`} icon={icon}>
        {text}
      </QuizToast>
    );
  };

  useScrollToTopFixIOS(scrollRef);

  // Exp 255 renders the stacked card treatment (icon centered above the label)
  // in a 2-up grid at every width; every other flow keeps the icon-left rows.
  const isTwoColumn = isMiniQuiz || isDesktop;
  const toggleVariant = isMiniQuiz
    ? ToggleVariant.EatingHabitsStacked
    : ToggleVariant.EatingHabits;
  const illustrationClass = isMiniQuiz
    ? eatingHabitsStyles['illustration-32-32']
    : eatingHabitsStyles['illustration-56-56'];

  return (
    <QuizQuestionScreen
      submitButtonDisabled={quiz.habits === null}
      testid={testid}
      heroText="What best describes how you eat?"
      sectionLabel={isMiniQuiz ? undefined : <SectionLabel label="About you" />}
      onSubmitAnalytics={
        isMiniQuiz
          ? undefined
          : () => {
              eatingHabitsAnalytics(quiz.habits);
            }
      }
      onSubmit={
        isMiniQuiz
          ? (nextQuiz) => {
              eatingHabitsAnalytics(nextQuiz.habits);
              onSubmit(nextQuiz);
            }
          : undefined
      }
    >
      <ControlSelect
        ariaLabel="Select your dietary preferences"
        question="habits"
        layoutVariant={
          isMiniQuiz ? 'two-column-desktop' : 'one-column-to-two-column'
        }
      >
        {OPTIONS.map((option) => (
          <ControlSelect.Option
            key={option.id}
            id={option.id}
            label={option.label}
          >
            <Toggle
              active={habits === null ? false : habits.includes(option.id)}
              testid={`${testid}-toggle-${option.slug}`}
              label={option.label}
              variant={toggleVariant}
              illustration={
                <div className={illustrationClass}>
                  <img
                    src={restrictionsImageMap[option.slug]}
                    alt={option.slug}
                  />
                </div>
              }
              subcopy={option.subcopy}
              className={
                styles[
                  isTwoColumn ? option.delayTwoColumn : option.delayOneColumn
                ]
              }
            />
          </ControlSelect.Option>
        ))}
      </ControlSelect>
    </QuizQuestionScreen>
  );
}
